import React from 'react';
import { Chat } from '../../../pages/Chat';
import { MembersSection } from './sections/MembersSection';
import { ControlCentreSection } from './sections/ControlCentreSection';
import { TrainerDashboardLayout } from './sections/TrainerDashboardLayout';
import { TrainerDashboardLibraryPane } from './sections/TrainerDashboardLibraryPane';
import { LibraryConfirmDialogs } from './overlays/LibraryConfirmDialogs';
import { AssignWorkoutPlanModal } from './overlays/AssignWorkoutPlanModal';
import { AssignWorkoutModal } from './overlays/AssignWorkoutModal';
import { ExerciseOverlayHost } from './overlays/ExerciseOverlayHost';
import { ExerciseEditSavedToast } from '../library/overlays/ExerciseEditSavedToast';
import { useTrainerDashboard } from './hooks/useTrainerDashboard';
import { accountNavDisplayName } from '../../../pages/account/accountShared';
import './styles/library.css';

/** Thin orchestrator — state/handlers in useTrainerDashboard; library UI in TrainerDashboardLibraryPane (R12). */
export const TrainerDashboard = () => {
  const dash = useTrainerDashboard();
  const { profile, user, activeSection, onSectionSelect, trainerGreetingFirstName, handleSignOut } = dash;

  const headerHidden =
    dash.createExerciseDrawerOpen
    || !!dash.editingExercise
    // Members redesign owns page title ("Members.") — hide shell h1 chrome on this section.
    || activeSection === 'members'
    || activeSection === 'chat';

  const headerTitle =
    activeSection === 'members'
      ? 'Member Management'
      : activeSection === 'library'
        ? 'Library'
        : trainerGreetingFirstName
          ? `Welcome back, ${trainerGreetingFirstName}!`
          : 'Welcome back!';

  return (
    <>
      <TrainerDashboardLayout
        activeSection={activeSection}
        onSectionSelect={onSectionSelect}
        headerHidden={headerHidden}
        headerTitle={headerTitle}
        onSignOut={handleSignOut}
        membersSlot={
          activeSection === 'members' && profile?.id ? (
            <MembersSection
              trainerId={profile.id}
              trainerName={accountNavDisplayName(profile, user, '')}
              pendingOpenAddMember={dash.pendingOpenAddMember}
              onPendingOpenAddMemberConsumed={() => dash.setPendingOpenAddMember(false)}
            />
          ) : null
        }
        chatSlot={activeSection === 'chat' ? <Chat embedded /> : null}
        mobileSlot={
          activeSection === 'library' ? (
            <TrainerDashboardLibraryPane variant="mobile" dash={dash} />
          ) : activeSection === 'control' ? (
            <ControlCentreSection />
          ) : null
        }
        desktopSlot={
          activeSection === 'library' ? (
            <TrainerDashboardLibraryPane variant="desktop" dash={dash} />
          ) : activeSection === 'control' ? (
            <ControlCentreSection />
          ) : null
        }
      />

      <AssignWorkoutPlanModal
        open={dash.showAssignModal}
        assigningPlanId={dash.assigningPlanId}
        planName={dash.workoutPlans.find((plan: { id: string; name?: string }) => plan.id === dash.assigningPlanId)?.name || 'Loading...'}
        assignMembers={dash.assignMembers}
        loadingAssignMembers={dash.loadingAssignMembers}
        selectedAssignMemberId={dash.selectedAssignMemberId}
        onSelectedAssignMemberIdChange={dash.setSelectedAssignMemberId}
        assignmentStartDate={dash.assignmentStartDate}
        onAssignmentStartDateChange={dash.setAssignmentStartDate}
        assignmentNotes={dash.assignmentNotes}
        onAssignmentNotesChange={dash.setAssignmentNotes}
        assignmentError={dash.assignmentError}
        isAssigning={dash.isAssigning}
        onClose={dash.handleCloseAssignModal}
        onSubmit={() => { void dash.handleAssignPlanSubmit(); }}
        onAddMember={dash.handleAddMemberFromEmpty}
      />

      <AssignWorkoutModal
        open={dash.showAssignWorkoutModal}
        assigningWorkoutId={dash.assigningWorkoutId}
        workoutName={
          dash.workouts.find((w: { id: string; name?: string }) => w.id === dash.assigningWorkoutId)?.name
          || 'Loading...'
        }
        assignMembers={dash.assignMembers}
        loadingAssignMembers={dash.loadingAssignMembers}
        selectedMemberId={dash.selectedAssignWorkoutMemberId}
        onSelectedMemberIdChange={dash.setSelectedAssignWorkoutMemberId}
        workoutDate={dash.workoutAssignmentDate}
        onWorkoutDateChange={dash.setWorkoutAssignmentDate}
        assignmentError={dash.assignWorkoutError}
        isAssigning={dash.isAssigningWorkout}
        onClose={dash.handleCloseAssignWorkoutModal}
        onSubmit={() => { void dash.handleAssignWorkoutSubmit(); }}
        onAddMember={dash.handleAddMemberFromEmptyWorkout}
      />

      {dash.assignPlanSuccessToast && (
        <ExerciseEditSavedToast
          variant="assign-plan"
          exerciseName=""
          planName={dash.assignPlanSuccessToast.planName}
          memberDisplayName={dash.assignPlanSuccessToast.memberDisplayName}
          onDismiss={() => dash.setAssignPlanSuccessToast(null)}
        />
      )}

      {dash.assignWorkoutSuccessToast && (
        <ExerciseEditSavedToast
          variant="assign-workout"
          exerciseName=""
          workoutName={dash.assignWorkoutSuccessToast.workoutName}
          memberDisplayName={dash.assignWorkoutSuccessToast.memberDisplayName}
          onDismiss={() => dash.setAssignWorkoutSuccessToast(null)}
        />
      )}

      {dash.workoutCreatedToast ? (
        <ExerciseEditSavedToast
          variant="workout-created"
          exerciseName={dash.workoutCreatedToast}
          onDismiss={() => dash.setWorkoutCreatedToast(null)}
        />
      ) : null}

      {dash.planCreatedToast ? (
        <ExerciseEditSavedToast
          variant="plan-created"
          exerciseName=""
          planName={dash.planCreatedToast}
          onDismiss={() => dash.setPlanCreatedToast(null)}
        />
      ) : null}

      <LibraryConfirmDialogs
        workoutDeleteConfirm={dash.workoutDeleteConfirm}
        onCloseWorkoutDelete={() => dash.setWorkoutDeleteConfirm(null)}
        onConfirmWorkoutDelete={(id) => { void dash.confirmDeleteWorkout(id); }}
        workoutPlanDeleteConfirm={dash.workoutPlanDeleteConfirm}
        onCloseWorkoutPlanDelete={() => dash.setWorkoutPlanDeleteConfirm(null)}
        onConfirmWorkoutPlanDelete={(id) => { void dash.confirmDeleteWorkoutPlan(id); }}
        exerciseDeleteConfirm={dash.exerciseDeleteConfirm}
        onCloseExerciseDelete={() => dash.setExerciseDeleteConfirm(null)}
        onConfirmExerciseDelete={(id) => { void dash.confirmDeleteExercise(id); }}
        workoutsBulkDeleteState={dash.workoutsBulkDeleteState}
        onCloseWorkoutsBulkDelete={() => dash.setWorkoutsBulkDeleteState(null)}
        onConfirmWorkoutsBulkDelete={() => { void dash.confirmBulkDeleteWorkouts(); }}
        workoutPlansBulkDeleteState={dash.workoutPlansBulkDeleteState}
        onCloseWorkoutPlansBulkDelete={() => dash.setWorkoutPlansBulkDeleteState(null)}
        onConfirmWorkoutPlansBulkDelete={() => { void dash.confirmBulkDeleteWorkoutPlans(); }}
        exercisesBulkDeleteState={dash.bulkDeleteState}
        onCloseExercisesBulkDelete={() => dash.setBulkDeleteState(null)}
        onConfirmExercisesBulkDelete={() => { void dash.handleConfirmBulkDeleteExercises(); }}
        bulkDeleteToast={dash.bulkDeleteToast}
      />

      <ExerciseOverlayHost
        activeSection={activeSection}
        viewingExercise={dash.viewingExercise}
        drawerIsEdit={dash.drawerIsEdit}
        createExerciseDrawerOpen={dash.createExerciseDrawerOpen}
        exerciseTagOptions={dash.exerciseTagOptions}
        exerciseDrawerTriggerRef={dash.exerciseDrawerTriggerRef}
        exerciseEditDrawerRef={dash.exerciseEditDrawerRef}
        createExerciseDrawerRef={dash.createExerciseDrawerRef}
        savedToastExerciseName={dash.savedToastExerciseName}
        savedToastVariant={dash.savedToastVariant}
        onDismissSavedToast={() => dash.setSavedToastExerciseName(null)}
        showExerciseEditWarningModal={dash.showExerciseEditWarningModal}
        pendingExerciseEditData={dash.pendingExerciseEditData}
        editingExercise={dash.editingExercise}
        exerciseEditMembersAffected={dash.exerciseEditMembersAffected}
        creatingExercise={dash.creatingExercise}
        onPerformExerciseEdit={(data, updateMembers) => { void dash.performExerciseEdit(data, updateMembers); }}
        onDismissExerciseEditWarning={() => {
          dash.setShowExerciseEditWarningModal(false);
          dash.setPendingExerciseEditData(null);
        }}
        onCloseExerciseDrawer={dash.closeExerciseDrawer}
        onRequestDrawerEdit={() => dash.setDrawerIsEdit(true)}
        onReturnToViewFromEdit={() => dash.setDrawerIsEdit(false)}
        onExerciseSaveFromDrawer={dash.handleExerciseSaveFromDrawer}
        onExerciseSaveComplete={(merged) => {
          dash.setViewingExercise(merged);
          dash.setDrawerIsEdit(false);
          dash.setSavedToastVariant('edit');
          dash.setSavedToastExerciseName(merged.name);
        }}
        onExerciseDiscardEdit={dash.handleExerciseDiscardEdit}
        onCreateExerciseFromDrawer={dash.handleCreateExerciseFromDrawer}
        onCloseCreateExerciseDrawer={dash.closeCreateExerciseDrawer}
        onCreateExerciseSaveComplete={(created) => {
          dash.ex.setLibExercisesPage(1);
          void dash.loadAllExercisesForLibrary({ silent: true });
          dash.setCreateExerciseDrawerOpen(false);
          dash.setSavedToastVariant('create');
          dash.setSavedToastExerciseName(created.name);
        }}
        showCompanionFormAtRoot={dash.shouldMountExerciseFormAtRoot}
        renderCompanionForm={dash.renderExerciseCompanionForm}
      />
    </>
  );
};
